import React from 'react';
import { Badge } from '../../components/ui/badge'; // Assuming badge is in ui folder
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'; // Assuming card components
import { cn } from '../../lib/utils'; // Assuming you have a utility for class names
import { getStatusBackgroundColor, getStatusBadgeStyles } from '../../utils/statusStyles';
import DefaultMapbox from '../Mapbox/DefaultMapbox'; // Adjust path if necessary
import * as turf from '@turf/turf';
import { House, Mail, MapPin } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Separator } from '../ui/separator';
import { WhatsAppOutlined } from '@ant-design/icons';
import { COLOR_DEFAULT } from '../../constants';

const getDistanceString = (
  lng1: number | string | undefined | null,
  lat1: number | string | undefined | null,
  lng2: number | string | undefined | null,
  lat2: number | string | undefined | null,
): string | null => {
  if (lng1 == null || lat1 == null || lng2 == null || lat2 == null) return null;

  try {
    const p1 = turf.point([Number(lng1), Number(lat1)]);
    const p2 = turf.point([Number(lng2), Number(lat2)]);
    const distance = turf.distance(p1, p2); // Distance in kilometers

    if (distance < 1) {
      return `${(distance * 1000).toFixed(0)} m`;
    } else {
      return `${distance.toFixed(1)} km`;
    }
  } catch (error) {
    console.error('Error calculating distance:', error);
    return null; // Handle potential errors during calculation (e.g., invalid coordinates)
  }
};

interface OrderLog {
  createdAt: string | Date;
  orderStatus: string; // Use string type, assuming status matches keys in ORDER_STATUSES
  notes?: string;
  lat?: string | null;
  lng?: string | null;
  event?: string;
}

interface Order {
  logs: OrderLog[];
  deliveryAddressLat?: string | null;
  deliveryAddressLng?: string | null;
}

interface OrderTimeLineProps {
  order: Order;
  tntSentEvents: any[];
}

const OrderTimeLine: React.FC<OrderTimeLineProps> = ({ order, tntSentEvents }) => {
  if (!order?.logs || order.logs.length === 0) {
    return <p>No order history available.</p>;
  }

  const EPOCH_TIMESTAMP = 1728518400728;

  const containsMoreInfo = (a, b) => {
    return Object.keys(a).some((ka) => !b[ka]);
  };

  // Sort logs chronologically (newest first)
  const sortedLogs = [...order.logs]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map((log) => ({
      ...log,
      createdAt: new Date(log.createdAt).getTime() === EPOCH_TIMESTAMP ? undefined : log.createdAt,
      status: log.orderStatus,
      event: log.event,
    }))
    .reduce((acc, log) => {
      if (/ETA gewijzigd/.test(log.notes)) return acc;
      const existingIndex = acc.findIndex(
        (l) =>
          (l.notes && log.notes && l.notes === log.notes) ||
          (l.orderStatus === log.orderStatus && [l.notes, log.notes].every((notes) => !notes)),
      );

      if (existingIndex !== -1) {
        // keep the best
        if (containsMoreInfo(log, acc[existingIndex])) {
          acc.splice(existingIndex, 1, log);
          return acc;
        }

        return acc;
      }
      // log.relatedLogs = [{ ...log }];
      return [...acc, log];
    }, []);

  const tntSentItems = tntSentEvents
    ?.filter((event) => event?.messageType?.includes('TRACK_AND_TRACE')) // This is a hack to filter out non-TNT events (like the whatsapp auto reply)
    .map((event) => ({
      color: '#' + COLOR_DEFAULT,
      createdAt: event.sentAt,
      children: (
        <>
          <span className="flex items-left gap-1">
            {event.channel === 'EMAIL' ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Mail className="w-4 h-4 text-gray-600" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Email{event.recipient ? ` (${event.recipient})` : ''}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}

            {event.channel === 'WHATSAPP' ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <WhatsAppOutlined className="w-4 h-4 text-gray-600" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Whatsapp{event.recipient ? ` (${event.recipient})` : ''}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            {/* <p style={{ fontSize: 'smaller', color: 'gray' }}>{!event.sentAt ? '' : formatDate(event.sentAt)}</p> */}
            <p className="text-xs text-gray-400 font-normal">{formatMessageType(event.messageType)} </p>
          </span>
        </>
      ),
    }));

  const items = [...sortedLogs, ...tntSentItems].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="space-y-4 relative">
      {/* Timeline line that spans the container but starts after the first dot */}
      <div className="absolute left-0 top-6 bottom-0 w-px border-l border-dashed border-gray-200"></div>

      {items.map((log, index) => {
        const distance = getDistanceString(log.lng, log.lat, order.deliveryAddressLng, order.deliveryAddressLat);
        return (
          <div key={index} className="relative">
            {/* Dot on the timeline */}
            <div
              className={`absolute left-0 top-5 h-2.5 w-2.5 rounded-full ${getStatusBackgroundColor(log.orderStatus)} z-10`}
              style={{ transform: 'translateX(-4px)' }}
            ></div>

            <Card className="ml-6 border-none shadow-none">
              <CardHeader className="p-4">
                <CardTitle className="flex items-center gap-2 text-sm font-medium min-w-0 overflow-visible">
                  {log.orderStatus ? (
                    <Badge
                      variant="outline"
                      className={cn('text-sm whitespace-nowrap', getStatusBadgeStyles(log.orderStatus))}
                    >
                      {formatOrderStatus(log.orderStatus) || 'Unknown Status'}
                    </Badge>
                  ) : (
                    log.children
                  )}

                  <div className="flex-grow"></div>

                  {log.lat != null &&
                    log.lng != null &&
                    order.deliveryAddressLat != null &&
                    order.deliveryAddressLng != null && (
                      <div className="flex items-center space-x-1 text-xs text-gray-500 whitespace-nowrap">
                        {distance && <p className="text-black text-s">{distance}</p>}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <MapPin className="h-3 w-3 hover:cursor-pointer text-black" />
                            </TooltipTrigger>
                            <TooltipContent className="w-[200px] h-[200px] p-0" side="bottom">
                              <div className="w-full h-full">
                                <DefaultMapbox
                                  markers={[
                                    {
                                      content: (
                                        <div>
                                          <MapPin className="h-6 w-6 text-blue-500" />
                                        </div>
                                      ),
                                      latitude: Number(log.lat),
                                      longitude: Number(log.lng),
                                      offsetX: 0,
                                      offsetY: 0,
                                    },
                                    {
                                      content: (
                                        <div>
                                          <House className="h-6 w-6 text-black" />
                                        </div>
                                      ),
                                      latitude: Number(order.deliveryAddressLat),
                                      longitude: Number(order.deliveryAddressLng),
                                      offsetX: 0,
                                      offsetY: 0,
                                    },
                                  ]}
                                  className="rounded-md overflow-hidden"
                                  isControl={false} // Hide navigation controls inside tooltip
                                />
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}

                  <span className="text-xs text-gray-300 whitespace-nowrap">{formatDate(log.createdAt)}</span>
                </CardTitle>
              </CardHeader>
              {log.notes && (
                <CardContent className="text-s text-gray-400 pb-4">
                  <p>{log.notes}</p>
                </CardContent>
              )}
              {items.length !== index + 1 && <Separator className="bg-gray-100" />}
            </Card>
          </div>
        );
      })}
    </div>
  );
};

export default OrderTimeLine;

export const formatOrderStatus = (reason) => {
  return reason
    ?.split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export function formatDate(dateString) {
  const date = new Date(dateString);

  const formatter = new Intl.DateTimeFormat('en-US', {
    // year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    // second: '2-digit',
    timeZone: 'Europe/Amsterdam', // Set the time zone to Amsterdam
    hour12: false, // Ensure 24-hour format
  });
  return formatter.format(date);
}

function formatMessageType(messageType) {
  // Convert underscores to spaces and capitalize first letter of each word
  if (!messageType) return '';

  // Replace underscores with spaces
  const withSpaces = messageType.replace(/_/g, ' ');

  // Capitalize first letter of each word
  return withSpaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .replace('And', '&');
}
